import { Toast } from "primereact/toast";
import { MasterTabs } from "../MasterTabs";
import { useEffect, useRef, useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";

import { getAllDetails, postAllData } from "../../../service/ProductService";
import { ADD_SOIL_TEST_PARAMETER, GET_LIST_SOIL_PARAMETER } from "../../../config/ApiConstant";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { SoilTestValidationSchema } from "../../../config/Validate";
import { parameterOptions } from "../../../appconfig/Settings";
import { Divider } from "primereact/divider";
type Level = "low" | "medium" | "high";


const SoilTestParameter = () => {
    document.title = "Soil Test Parameter | Agriown";

    const toast = useRef<Toast>(null);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);

    // fields for Recommendations
    const recommendationsFields = () => ({
        parameter: null,
        source: "",
        dose: "",
        method: "",
    });

    // fields for advisory
    const advisoryFields = () => ({
        parameter: null,
        issue_identified: "",
        recommended_advisory: "",
    });

    const initialValues = {
        soil_recommendation: {
            low: [recommendationsFields()],
            medium: [recommendationsFields()],
            high: [recommendationsFields()],
        },

        soil_advisory: {
            low: [advisoryFields()],
            medium: [advisoryFields()],
            high: [advisoryFields()],
        },
    };

    const [formData, setFormData] = useState(initialValues);

    // map api values to formik values
    const mapApiToFormikValues = (apiData: any) => {
        const recommendationsFields = () => ({
            parameter: null,
            source: "",
            dose: "",
            method: "",
        });

        const result: {
            soil_recommendation: Record<Level, any[]>;
            soil_advisory: Record<Level, any[]>;
        } = {
            soil_recommendation: {
                low: [],
                medium: [],
                high: [],
            },
            soil_advisory: {
                low: [],
                medium: [],
                high: [],
            },
        };


        // Soil Recommendation
        apiData?.soil_recommendation?.forEach((item: any) => {
            const level = item.recommendation_type.toLowerCase() as Level;

            if (!result.soil_recommendation[level]) return;

            result.soil_recommendation[level].push({
                parameter: item.recommendation_parameter,
                source: item.recommended_source,
                dose: item.dose_per_acre,
                method: item.method_of_application,
            });
        });

        // Soil Advisory
        apiData?.soil_advisory?.forEach((item: any) => {
            const level = item.advisory_type.toLowerCase() as Level;

            if (!result.soil_advisory[level]) return;

            result.soil_advisory[level].push({
                parameter: item.advisory_parameter?.toLowerCase() ?? null,
                issue_identified: item.issue_identified ?? "",
                recommended_advisory: item.recommended_advisory ?? "",
            });
        });

        // Ensure at least one row exists
        (["low", "medium", "high"] as const).forEach((level) => {
            if (result.soil_recommendation[level].length === 0) {
                result.soil_recommendation[level].push(recommendationsFields());
            }
            if (result.soil_advisory[level].length === 0) {
                result.soil_advisory[level].push(advisoryFields());
            }
        });

        return result;
    };


    useEffect(() => {
        const fetchSoilTest = async () => {
            setPageLoading(true);
            try {
                const response = await getAllDetails(GET_LIST_SOIL_PARAMETER);

                const apiData =
                    response?.data?.data ??
                    response?.data ??
                    response;

                const mappedData = mapApiToFormikValues(apiData);

                setFormData(mappedData);
            } catch (e) {
                console.error(e);
            } finally {
                setPageLoading(false);
            }
        };

        fetchSoilTest();
    }, []);


    // map Recommendations array of objects
    const mapRecommendations = (recommendation: any) => {
        const result: any[] = [];

        Object.entries(recommendation).forEach(([level, items]: any) => {
            items.forEach((item: any) => {
                if (!item.parameter) return;

                result.push({
                    recommendation_type: level.charAt(0).toUpperCase() + level.slice(1),
                    recommendation_parameter: item.parameter,
                    recommended_source: item.source,
                    dose_per_acre: item.dose,
                    method_of_application: item.method,
                });
            });
        });

        return result;


    };

    // map Advisory array of objects
    const mapAdvisory = (advisory: any) => {
        const result: any[] = [];

        Object.entries(advisory).forEach(([level, items]: any) => {
            items.forEach((item: any) => {
                if (!item.parameter) return;

                result.push({
                    advisory_type: level.charAt(0).toUpperCase() + level.slice(1),
                    advisory_parameter: item.parameter,
                    issue_identified: item.issue_identified,
                    recommended_advisory: item.recommended_advisory,
                });
            });
        });

        return result;
    };

    // handle submition
    const handleSubmit = async (values: any, { resetForm }: any) => {
        setLoading(true);

        try {
            const payload = {
                soil_recommendation: mapRecommendations(values.soil_recommendation),
                soil_advisory: mapAdvisory(values.soil_advisory),
            };
            const payloadsss = {
                soil_recommendation: values.soil_recommendation,
                soil_advisory: values.soil_advisory,
            };

            console.log("API Payload:", payload);
            console.log("payloadssspayloadsss:", payloadsss);

            const response = await postAllData(ADD_SOIL_TEST_PARAMETER, payload);

            setFormData(values);
            if (response) {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: response.message,
                });
            }

        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: error?.response?.data?.message || "Something went wrong",
            });
        } finally {
            setLoading(false);
        }
    };

    // map Advisory Section fields for low, medium and high
    const AdvisorySection = ({
        title,
        name,
        values,
        setFieldValue,
        errors,
        touched,
    }: any) => (
        <div className="col-12">
            <h4 className="mt-5">{title}</h4>

            <FieldArray name={name}>
                {({ push, remove }) => (
                    <>
                        {values.map((_: any, index: number) => {
                            const rowErrors =
                                errors?.[name.split(".")[0]]?.[name.split(".")[1]]?.[index];
                            const rowTouched =
                                touched?.[name.split(".")[0]]?.[name.split(".")[1]]?.[index];

                            return (
                                <div className="grid p-fluid mb-3" key={index}>
                                    {/* Parameter */}
                                    <div className="col-4">
                                        <label>Parameter *</label>
                                        <Dropdown
                                            options={parameterOptions}
                                            placeholder="Select"
                                            value={values[index].parameter}
                                            onChange={(e) =>
                                                setFieldValue(`${name}.${index}.parameter`, e.value)
                                            }
                                            className={
                                                rowTouched?.parameter && rowErrors?.parameter
                                                    ? "p-invalid"
                                                    : ""
                                            }
                                        />
                                        {rowTouched?.parameter && rowErrors?.parameter && (
                                            <small className="p-error">
                                                {rowErrors.parameter}
                                            </small>
                                        )}
                                    </div>

                                    {/* Issue Identified */}
                                    <div className="col-4">
                                        <label>Issue Identified *</label>
                                        <InputText
                                            value={values[index].issue_identified}
                                            onChange={(e) =>
                                                setFieldValue(
                                                    `${name}.${index}.issue_identified`,
                                                    e.target.value
                                                )
                                            }
                                            className={
                                                rowTouched?.issue_identified &&
                                                    rowErrors?.issue_identified
                                                    ? "p-invalid"
                                                    : ""
                                            }
                                        />
                                        {rowTouched?.issue_identified &&
                                            rowErrors?.issue_identified && (
                                                <small className="p-error">
                                                    {rowErrors.issue_identified}
                                                </small>
                                            )}
                                    </div>

                                    {/* Recommended Advisory */}
                                    <div className="col-3">
                                        <label>Recommended Advisory *</label>
                                        <InputText
                                            value={values[index].recommended_advisory}
                                            onChange={(e) =>
                                                setFieldValue(
                                                    `${name}.${index}.recommended_advisory`,
                                                    e.target.value
                                                )
                                            }
                                            className={
                                                rowTouched?.recommended_advisory &&
                                                    rowErrors?.recommended_advisory
                                                    ? "p-invalid"
                                                    : ""
                                            }
                                        />
                                        {rowTouched?.recommended_advisory &&
                                            rowErrors?.recommended_advisory && (
                                                <small className="p-error">
                                                    {rowErrors.recommended_advisory}
                                                </small>
                                            )}
                                    </div>

                                    <div className="col-1 flex align-items-end">
                                        {index > 0 && (
                                            <Button
                                                icon="pi pi-trash"
                                                className="p-button-danger p-button-sm"
                                                type="button"
                                                onClick={() => remove(index)}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        <Button
                            label="Add More"
                            icon="pi pi-plus"
                            className="p-button-success w-1"
                            type="button"
                            onClick={() => push(advisoryFields())}
                        />
                    </>
                )}
            </FieldArray>
        </div>
    );


    // map Recommendation Section fields for low, medium and high
    const RecommendationSection = ({
        title,
        name,
        values,
        setFieldValue,
        errors,
        touched,
    }: any) => (
        <div className="col-12">
            <h4 className="mt-5">{title}</h4>

            <FieldArray name={name}>
                {({ push, remove }) => (
                    <>
                        {values.map((_: any, index: number) => {
                            const rowErrors = errors?.[name?.split(".")[0]]?.[name?.split(".")[1]]?.[index];
                            const rowTouched = touched?.[name?.split(".")[0]]?.[name?.split(".")[1]]?.[index];

                            return (
                                <div className="grid p-fluid mb-3" key={index}>
                                    {/* Parameter */}
                                    <div className="col-3">
                                        <label>Parameter *</label>
                                        <Dropdown
                                            options={parameterOptions}
                                            placeholder="Select"
                                            value={values[index].parameter}
                                            onChange={(e) =>
                                                setFieldValue(`${name}.${index}.parameter`, e.value)
                                            }
                                            className={
                                                rowTouched?.parameter && rowErrors?.parameter
                                                    ? "p-invalid"
                                                    : ""
                                            }
                                        />
                                        {rowTouched?.parameter && rowErrors?.parameter && (
                                            <small className="p-error">{rowErrors.parameter}</small>
                                        )}
                                    </div>

                                    {/* Source */}
                                    <div className="col-3">
                                        <label>Recommended Source *</label>
                                        <InputText
                                            value={values[index].source}
                                            onChange={(e) =>
                                                setFieldValue(`${name}.${index}.source`, e.target.value)
                                            }
                                            className={
                                                rowTouched?.source && rowErrors?.source ? "p-invalid" : ""
                                            }
                                        />
                                        {rowTouched?.source && rowErrors?.source && (
                                            <small className="p-error">{rowErrors.source}</small>
                                        )}
                                    </div>

                                    {/* Dose */}
                                    <div className="col-3">
                                        <label>Dose per Acre *</label>
                                        <InputText
                                            value={values[index].dose}
                                            onChange={(e) =>
                                                setFieldValue(`${name}.${index}.dose`, e.target.value)
                                            }
                                            className={
                                                rowTouched?.dose && rowErrors?.dose ? "p-invalid" : ""
                                            }
                                        />
                                        {rowTouched?.dose && rowErrors?.dose && (
                                            <small className="p-error">{rowErrors.dose}</small>
                                        )}
                                    </div>

                                    {/* Method */}
                                    <div className="col-2">
                                        <label>Method of Application *</label>
                                        <InputText
                                            value={values[index].method}
                                            onChange={(e) =>
                                                setFieldValue(`${name}.${index}.method`, e.target.value)
                                            }
                                            className={
                                                rowTouched?.method && rowErrors?.method ? "p-invalid" : ""
                                            }
                                        />
                                        {rowTouched?.method && rowErrors?.method && (
                                            <small className="p-error">{rowErrors.method}</small>
                                        )}
                                    </div>

                                    <div className="col-1 flex align-items-end">
                                        {index > 0 && (
                                            <Button
                                                icon="pi pi-trash"
                                                className="p-button-danger p-button-sm"
                                                type="button"
                                                onClick={() => remove(index)}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}

                        <Button
                            label="Add More"
                            icon="pi pi-plus"
                            className="p-button-success w-1"
                            type="button"
                            onClick={() => push(recommendationsFields())}
                        />
                    </>
                )}
            </FieldArray>
        </div>
    );



    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <MasterTabs index={4} />
                    <Toast ref={toast} />

                    <Formik
                        initialValues={formData}
                        enableReinitialize
                        validationSchema={SoilTestValidationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            setFieldValue,
                            // setFieldTouched,ś
                        }) => (
                            <Form className="grid p-fluid">
                                {/* Recommendations */}
                                <div className="w-full">
                                    <div className="mb-6">
                                        <h3 style={{ paddingLeft: "7px", margin: '0px' }}>AgriOwn Recommendations: </h3>
                                        <RecommendationSection
                                            title="1. Low"
                                            name="soil_recommendation.low"
                                            values={values.soil_recommendation.low}
                                            setFieldValue={setFieldValue}
                                            errors={errors}
                                            touched={touched}
                                        />

                                        <RecommendationSection
                                            title="2. Medium"
                                            name="soil_recommendation.medium"
                                            values={values.soil_recommendation.medium}
                                            setFieldValue={setFieldValue}
                                            errors={errors}
                                            touched={touched}
                                        />

                                        <RecommendationSection
                                            title="3. High"
                                            name="soil_recommendation.high"
                                            values={values.soil_recommendation.high}
                                            setFieldValue={setFieldValue}
                                            errors={errors}
                                            touched={touched}
                                        />
                                    </div>

                                    {/* Advisory Notes */}
                                    <>
                                        <Divider />
                                        <div className="mt-6">
                                            <h3 style={{ paddingLeft: "7px" }}> AgriOwn Advisory Notes: </h3>

                                            <AdvisorySection
                                                title="1. Low"
                                                name="soil_advisory.low"
                                                values={values.soil_advisory.low}
                                                setFieldValue={setFieldValue}
                                                errors={errors}
                                                touched={touched}
                                            />

                                            <AdvisorySection
                                                title="2. Medium"
                                                name="soil_advisory.medium"
                                                values={values.soil_advisory.medium}
                                                setFieldValue={setFieldValue}
                                                errors={errors}
                                                touched={touched}
                                            />

                                            <AdvisorySection
                                                title="3. High"
                                                name="soil_advisory.high"
                                                values={values.soil_advisory.high}
                                                setFieldValue={setFieldValue}
                                                errors={errors}
                                                touched={touched}
                                            />
                                        </div>
                                    </>

                                    {/* Submit */}
                                    <div className="col-12">
                                        <div className="flex justify-content-end">
                                            <Button
                                                label="Save"
                                                icon="pi pi-check"
                                                type="submit"
                                                className="w-1"
                                                loading={loading}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
};

export default SoilTestParameter;
